//Features.tsx
import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const FeatureCard = styled(Card)(({ theme }) => ({      
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',   
  padding: theme.spacing(2),
  height: '65vh',
  boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.5)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '8px 8px 4px rgba(0, 0, 0, 0.5)',
  },
}));
const FeatureImage = styled(CardMedia)(({ theme }) => ({
  width: '100%',
  height: 200,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
}));
const FeatureTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontWeight: 'bold',
  fontSize: '1.5rem',
}));
const FeatureDescription = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  textAlign: 'left',
}));

interface Feature {
  title: string;
  description: string;
  image: string;
}
const features: Feature[] = [
  {
    title: 'STB不要',
    description: 'WEB技術を活用し、<br/>STBなしでの配信・閲覧を可能にしました。<br/>これにより、設置コスト削減と導入の簡素化、端末管理の最適化を実現します。',
    image: '/images/Features/1.png',
  },
  {
    title: '専用モニター不要',
    description: '既存のモニターを活用し、タブレット・スマートフォン・PCモニターでの閲覧を可能にしました。<br/>専用アプリも不要でブラウザからの閲覧が可能です。<br/>これにより、導入コスト削減と設置工事の簡素化・不要化を実現します。',
    image: '/images/Features/2.png',
  },
  {
    title: '直感的なコンテンツ作成',
    description: '独自開発のコンテンツ作成ツール(CMS)を使用し、誰でも簡単にコンテンツを作成できます。<br/>直感的な操作で、専門知識がなくても魅力的なコンテンツを作成可能です。',
    image: '/images/Features/3.png',
  }
];

const Features: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
      <Container 
        maxWidth="lg" 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: theme.spacing(4),
          width: '100vw',
          height: '100vh',
          padding: theme.spacing(4),
        }}>
        <Typography variant="h4" gutterBottom> 
            製品特徴
        </Typography>
        {isMobile ? (
          <Swiper
            modules={[Pagination, Navigation]}
            spaceBetween={30}
            slidesPerView={1}
            pagination={{ clickable: true }}
            navigation
            breakpoints={{
              600: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              900: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
            }}
            style={{ width: '80vw', paddingBottom: '50px' }}
          >
            {features.map((feature, index) => (
              <SwiperSlide 
                key={index}
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <FeatureCard>
                  <FeatureImage image={feature.image} title={feature.title} />
                  <CardContent>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>
                      {feature.description.split('<br/>').map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < feature.description.split('<br/>').length - 1 && <br />}
                        </span>
                      ))}
                    </FeatureDescription>
                  </CardContent>
                </FeatureCard>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={4} sm={6} md={4} key={index}>
                <FeatureCard>
                  <FeatureImage image={feature.image} title={feature.title} />
                  <CardContent>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>
                      {feature.description.split('<br/>').map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < feature.description.split('<br/>').length - 1 && <br />}
                        </span>
                      ))}
                    </FeatureDescription>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
  );
}
export default Features;
